import React, { useCallback, useEffect } from 'react';
import { styled } from 'baseui';
import { toaster } from 'baseui/toast';
import { useAtomValue } from 'jotai';
import { ANNOTATION_EVENT_NAMES } from '../../../../analytics/event-name';
import useAnalytics from '../../../../analytics/useAnalytics';
import ToolbarButton, { TOOLBAR_BUTTON_SIZE } from '../../../../common/toolbar-button';
import type { AnnotationLabelT } from '../../../../common/types/types';
import { useLogger } from '../../../../modernization-common/hooks/use-logger';
import { MergePolygon } from '../../../icons/merge-polygon';
import { SubtractPolygon } from '../../../icons/subtract-polygon';
import { useStageAtoms } from '../../../labelling-v2/components/stage-scope/hooks';
import { ANNOTATION_TYPE } from '../../constants';
import useShortcut from '../../providers/hooks/use-shortcut';
import useStage from '../../providers/hooks/use-stage';
import type { StageAnnotation } from '../../providers/stage-provider/types';
import { handleShortcutRegistrationError } from '../../providers/utils/shortcut';
import PanelContainer from './panel-container';
const resolveAnnotations = (ids: string[], store: Record<string, StageAnnotation>, logger?: {
  error: (message: string, context?: Record<string, string>) => void;
}): StageAnnotation[] => {
  const result: StageAnnotation[] = [];
  for (const id of ids) {
    const annotation = store[id];
    if (!annotation) {
      const message = `Annotation with id "${id}" not found in annotationStore`;
      logger?.error(message, {
        annotationId: id
      });
      continue;
    }
    result.push(annotation);
  }
  return result;
};
const PANEL_MAX_WIDTH = 100;
const Icons = styled('div', {
  display: 'flex',
  justifyContent: 'flex-start',
  margin: '4px 0px'
});
export const MERGE_POLYGON_ERROR_MESSAGE = `Select at least two annotations of the same shape (polygon or rectangle only) to merge.`;
export const SUBTRACT_POLYGON_ERROR_MESSAGE = `Select at least two annotations of the same shape (polygon or rectangle only) to subtract.`;
export const WRONG_LABELS_ERROR_MESSAGE = `Only annotations of the same class can be merged. Please adjust your selection.`;
const isAnnotationsInvalid = (selectedAnnotations: {
  annotationType: (typeof ANNOTATION_TYPE)[keyof typeof ANNOTATION_TYPE];
}[]) => {
  return selectedAnnotations.some(({
    annotationType
  }) => annotationType !== ANNOTATION_TYPE.POLYGON && annotationType !== ANNOTATION_TYPE.RECTANGLE);
};
const isLabelDifferent = (selectedAnnotations: {
  annotationLabel: AnnotationLabelT;
}[]) => {
  const label = selectedAnnotations[0]?.annotationLabel.title;
  return selectedAnnotations.some(({
    annotationLabel
  }) => annotationLabel.title !== label);
};
export const MultiSelectPanel = () => {
  const {
    mergePolygon,
    subtractPolygon
  } = useStage();
  const {
    annotationStoreAtom,
    selectedAnnotationIdsAtom
  } = useStageAtoms();
  const annotationStore = useAtomValue(annotationStoreAtom);
  const selectedAnnotationIds = useAtomValue(selectedAnnotationIdsAtom);
  const {
    registerHotKeys
  } = useShortcut();
  const {
    sendEvent
  } = useAnalytics();
  const logger = useLogger();
  const handleMergePolygon = useCallback(() => {
    const selectedAnnotations = resolveAnnotations(selectedAnnotationIds, annotationStore, logger);
    if (selectedAnnotations.length < 2 || isAnnotationsInvalid(selectedAnnotations)) {
      toaster.negative(MERGE_POLYGON_ERROR_MESSAGE);
      return;
    }
    if (isLabelDifferent(selectedAnnotations)) {
      toaster.negative(WRONG_LABELS_ERROR_MESSAGE);
      return;
    }
    mergePolygon();
    sendEvent({
      action: ANNOTATION_EVENT_NAMES.MERGE_POLYGON,
      payload: {
        annotationCount: selectedAnnotations.length
      }
    });
  }, [annotationStore, selectedAnnotationIds, logger, mergePolygon, sendEvent]);
  const handleSubtractPolygon = useCallback(() => {
    const selectedAnnotations = resolveAnnotations(selectedAnnotationIds, annotationStore, logger);
    if (selectedAnnotations.length < 2 || isAnnotationsInvalid(selectedAnnotations)) {
      toaster.negative(SUBTRACT_POLYGON_ERROR_MESSAGE);
      return;
    }
    subtractPolygon();
    sendEvent({
      action: ANNOTATION_EVENT_NAMES.SUBTRACT_POLYGON,
      payload: {
        annotationCount: selectedAnnotations.length
      }
    });
  }, [annotationStore, selectedAnnotationIds, logger, sendEvent, subtractPolygon]);
  useEffect(() => {
    const {
      errors,
      removeHotKeys
    } = registerHotKeys([{
      keys: ['Shift', 'M'],
      onAction: handleMergePolygon
    }, {
      keys: ['Shift', 'S'],
      onAction: handleSubtractPolygon
    }]);
    errors.forEach(error => handleShortcutRegistrationError(error));
    return () => {
      removeHotKeys();
    };
  }, [handleMergePolygon, handleSubtractPolygon, registerHotKeys]);
  return <PanelContainer width={PANEL_MAX_WIDTH}>
      <Icons>
        <ToolbarButton title="Merge Polygon" onClick={handleMergePolygon} attributes={{
        'data-testid': 'merge-polygon'
      }} size={TOOLBAR_BUTTON_SIZE.REGULAR}>
          <MergePolygon />
        </ToolbarButton>
        <ToolbarButton title="Subtract Polygon" onClick={handleSubtractPolygon} attributes={{
        'data-testid': 'subtract-polygon'
      }} size={TOOLBAR_BUTTON_SIZE.REGULAR}>
          <SubtractPolygon />
        </ToolbarButton>
      </Icons>
    </PanelContainer>;
};